import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Button, LoadingIndicator, WideAlert, Icons } from '@components';
import { useNavigate } from 'react-router-dom';
import { TransformacaoBeneficioService } from '@services';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import { mdiCommentAlertOutline } from '@mdi/js';
import { DadosBeneficio } from './DadosBeneficio';
import { ResumoRecibmento } from './ResumoRecebimento';
import { SolicitacaoFormaRecebimento } from './SolicitacaoFormaRecebimento';
import { AlertaProcessoSolicitacao } from './AlertaProcessoSolicitacao';
import { TermoConsentimento } from './TermoConsentimento';

const service = new TransformacaoBeneficioService();
const steps = ['1', '2', '3'];
const VALOR_MINIMO_TRANSFORMACAO = 45902.24;

export const TransformacaoBeneficiosPage = () => {
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [atingiuValorMinimo, setAtingiuValorMinimo] = useState(true);
  const [exibirResumo, setExibirResumo] = useState(true);
  const [mensagemBloqueio, setMensagemBloqueio] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());

  const [dados, setDados] = useState({
    percRenda: 0,
    rendaMensalInicial: 0,
  });

  const [dadosIniciais, setDadosIniciais] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);

    service
      .getDadosTransformacaoBeneficio(user.matricula)
      .then((res) => {
        if (res.codConsistencia !== '7') {
          setExibirResumo(false);
          setMensagemBloqueio(
            'O período de solicitação para a transformação da forma de recebimento ' +
              'do benefício vitalício em financeiro foi encerrado em 28/04/2023'
          );
        } else if (res.reservaMatematica <= VALOR_MINIMO_TRANSFORMACAO) {
          setAtingiuValorMinimo(false);
          setMensagemBloqueio(
            'Você não é elegível à transformação de benefício.' +
              'Para mais esclarecimentos entre em contato com o atendimento Forluz'
          );
        } else {
          setDadosIniciais(res);
        }
      })
      .catch(() => {
        toast.error('Erro ao obter dados');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user.matricula, activeStep]);

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  useEffect(() => {
    window.scroll(0, 0);
  }, [activeStep]);

  const salvaDados = (values) => {
    setDados(values);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return !loading ? (
    <>
      <Helmet>
        <title>Forluz - Transformação de benefícios aqui</title>
      </Helmet>
      <h1 className="page-title mb-3">
        <strong>Transformação de Benefícios</strong>
      </h1>
      <hr className="my-4" />
      <div className="d-flex col-3 col-md-2 col-lg-1 justify-content-center">
        <Button
          className="justify-content-center mb-32"
          transparent
          color="orange"
          icon="arrow_back"
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </div>

      {atingiuValorMinimo && exibirResumo ? (
        <div className="card">
          <div className="card-body">
            {dadosIniciais.codConsistencia === '7' ? (
              <>
                <DadosBeneficio dadosIniciais={dadosIniciais} />
                <hr className="mb-32" />
                <ResumoRecibmento dadosIniciais={dadosIniciais} />
              </>
            ) : (
              <Box sx={{ width: '100%' }}>
                <Stepper activeStep={activeStep}>
                  {steps.map((label, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    if (isStepSkipped(index)) {
                      stepProps.completed = false;
                    }
                    return (
                      <Step
                        key={label}
                        sx={{
                          '& .MuiStepLabel-root .Mui-completed': {
                            color: '#EB943C',
                          },
                          '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel':
                            {
                              color: 'grey.500',
                            },
                          '& .MuiStepLabel-root .Mui-active': {
                            color: '#EB943C',
                          },
                          '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel':
                            {
                              color: 'common.white',
                            },
                          '& .MuiStepLabel-root .Mui-active .MuiStepIcon-text':
                            {
                              fill: 'black',
                            },
                          marginLeft: '25px',
                          marginRight: '25px',
                        }}
                        {...stepProps}
                      >
                        <StepLabel {...labelProps} />
                      </Step>
                    );
                  })}
                </Stepper>
                {activeStep === steps.length && (
                  <div className="mb-2">
                    <Typography sx={{ mt: 2, mb: 1 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        margin: '50px',
                        marginRight: '50px',
                        pt: 2,
                      }}
                    >
                      <Box sx={{ flex: '1 1 auto' }} />
                      <div className="row">
                        <Button onClick={handleReset}>Simular novamente</Button>
                      </div>
                    </Box>
                  </div>
                )}
                {activeStep === 0 && (
                  <>
                    {(dadosIniciais.codConsistencia === '0' ||
                      dadosIniciais.codConsistencia === '6') && (
                      <DadosBeneficio dadosIniciais={dadosIniciais} />
                    )}
                    <hr className="mt-32 mb-32" />
                    {(dadosIniciais.codConsistencia === '0' ||
                      dadosIniciais.codConsistencia === '6') && (
                      <SolicitacaoFormaRecebimento
                        dadosIniciais={dadosIniciais}
                        callback={handleNext}
                        salvaDados={salvaDados}
                      />
                    )}
                    {dadosIniciais.codConsistencia !== '0' &&
                      dadosIniciais.codConsistencia !== '6' &&
                      dadosIniciais.codConsistencia !== '7' && (
                        <WideAlert className="mb-4" iconClear={false}>
                          <div className="d-block d-lg-flex align-items-center">
                            <Icons
                              path={mdiCommentAlertOutline}
                              color="var(--primary-color)"
                              size={2}
                              style={{ marginRight: 4 }}
                            />
                            <p className="ts-16">
                              <strong>Aviso importante:</strong>{' '}
                              {dadosIniciais.mensagemAviso}
                            </p>
                          </div>
                        </WideAlert>
                      )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        pt: 2,
                        marginTop: '0px',
                        marginLeft: '25px',
                        marginRight: '25px',
                        marginBottom: '25px',
                      }}
                    >
                      <Box sx={{ flex: '1 1 auto' }} />
                    </Box>
                  </>
                )}
                {activeStep === 1 && (
                  <div className="mb-2 ms-sm-4 mt-32">
                    <AlertaProcessoSolicitacao />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        pt: 2,
                        marginTop: '0px',
                        marginLeft: '25px',
                        marginRight: '25px',
                        marginBottom: '25px',
                      }}
                    >
                      <div className="row mt-32">
                        <Button onClick={handleBack}>Anterior</Button>
                      </div>
                      <Box sx={{ flex: '1 1 auto' }} />

                      {activeStep !== steps.length - 1 && (
                        <div className="row mt-32">
                          <Button onClick={handleNext}>Próximo</Button>
                        </div>
                      )}
                    </Box>
                  </div>
                )}
                {activeStep === 2 && (
                  <div className="mt-32 mx-4">
                    <TermoConsentimento
                      dadosIniciais={dadosIniciais}
                      dados={dados}
                      callback={setActiveStep}
                      prevActiveStep
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        pt: 2,
                        marginTop: '0px',
                        marginLeft: '25px',
                        marginRight: '25px',
                        marginBottom: '25px',
                      }}
                    >
                      {activeStep !== steps.length - 1 && (
                        <div className="row mt-32">
                          <Button onClick={handleBack}>Anterior</Button>
                        </div>
                      )}
                      <Box sx={{ flex: '1 1 auto' }} />

                      {activeStep !== steps.length - 1 && (
                        <div className="row mt-32">
                          <Button onClick={handleNext}>Próximo</Button>
                        </div>
                      )}
                    </Box>
                  </div>
                )}
              </Box>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <WideAlert className="mt-32 mb-32" iconClear={false}>
              <div className="d-block d-lg-flex align-items-center">
                <Icons
                  path={mdiCommentAlertOutline}
                  color="var(--primary-color)"
                  size={2}
                  style={{ marginRight: 4 }}
                />
                <p className="ts-16">
                  <strong>Aviso importante:</strong> {mensagemBloqueio}
                </p>
              </div>
            </WideAlert>
          </div>
        </div>
      )}
    </>
  ) : (
    <LoadingIndicator />
  );
};
